import { useEffect, useMemo, useState } from 'react'
import {
  listAdmissionFormSettings,
  deleteAdmissionFormSetting,
} from '../api/admissionFormSettingApi'
import { loadMasterOptions } from '../../students/api/mastersApi'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { appToast } from '../../../../components/AppToast'

const DEFAULT_PAGE_SIZE = 8

export function useAdmissionFormSettingListScreen() {
  const [searchField, setSearchField] = useState('Name')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [masterOptions, setMasterOptions] = useState({ institutes: [], branches: [] })
  const [searchText, setSearchText] = useState('')
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])

  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  })

  const queryParams = useMemo(
    () => ({
      searchField,
      company: companyFilter,
      branch: branchFilter,
      searchText,
    }),
    [searchField, companyFilter, branchFilter, searchText],
  )

  const loadSettings = async () => {
    setIsLoading(true)
    setError('')

    try {
      const allSettings = await listAdmissionFormSettings(queryParams)
      const startIndex = (page - 1) * DEFAULT_PAGE_SIZE
      const paginated = allSettings.slice(startIndex, startIndex + DEFAULT_PAGE_SIZE)

      setItems(paginated)
      setMeta({
        totalCount: allSettings.length,
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: Math.max(1, Math.ceil(allSettings.length / DEFAULT_PAGE_SIZE)),
      })
    } catch (requestError) {
      setError('Failed to load admission form settings.')
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadSettings()
  }, [queryParams, page])

  useEffect(() => {
    loadMasterOptions(['institutes', 'branches'])
      .then(setMasterOptions)
      .catch(() => setMasterOptions({ institutes: [], branches: [] }))
  }, [])

  const resetFilters = () => {
    setSearchField('Name')
    setCompanyFilter('All')
    setBranchFilter('All')
    setSearchText('')
    setPage(1)
  }

  const handleDelete = async (settingId) => {
    const confirmed = await appConfirm({
      title: 'Delete Form Setting',
      message: 'Are you sure you want to permanently delete this admission form setting? Active application links using this template may be affected.',
      confirmText: 'Delete Setting',
      variant: 'danger',
    })
    if (!confirmed) return

    try {
      await deleteAdmissionFormSetting(settingId)
      appToast.success('Admission form setting deleted successfully')
      await loadSettings()
    } catch (requestError) {
      appToast.error('Failed to delete the form setting.')
      setError('Failed to delete the form setting.')
    }
  }

  return {
    state: {
      searchField,
      companyFilter,
      branchFilter,
      searchText,
      page,
      isLoading,
      error,
      rows: items,
      meta,
      options: {
        companyOptions: ['All', ...(masterOptions.institutes || [])],
        branchOptions: ['All', ...(masterOptions.branches || [])],
      },
    },
    actions: {
      setSearchField,
      setCompanyFilter: (value) => {
        setCompanyFilter(value)
        setPage(1)
      },
      setBranchFilter: (value) => {
        setBranchFilter(value)
        setPage(1)
      },
      setSearchText: (value) => {
        setSearchText(value)
        setPage(1)
      },
      setPage,
      resetFilters,
      handleDelete,
      refresh: loadSettings,
    },
  }
}
